import { Box, Grid, darken } from '@mui/material';

import { observer } from 'mobx-react-lite';
import moment from 'moment';
import { CSSProperties, SyntheticEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';

import dayjs from 'dayjs';
import { useLocation, useParams } from 'react-router-dom';
import LoadingComponent from '../../../components/LoadingComponent';
import { CalendarEvent } from '../../../data/models/event';
import { ModuleName } from '../../../data/models/role';
import { useStore } from '../../../stores/store';
import { generateColor } from '../../../utils/colorGenerator';
import { hasModulePermission } from '../../../utils/permissionEvaluator';
import Event from '../Components/Event';
import MonthHeader from '../Components/MonthHeader';
import Toolbar from '../Components/Toolbar';
import WeekHeader from '../Components/WeekHeader';
import EventForm from '../Form/EventForm';
import EventPopper from '../Form/EventPopper';
import BookingHeader from '../Options/BookingHeader';
import '../rbc.css';
import '../styles.css';
import CalendarDashboard from './CalendarDashboard';
import EventList from './EventList';

const localizer = momentLocalizer(moment);
moment.updateLocale('en', {
    week: {
        dow: 1, // Monday is the first day of the week
    },
});

function MyCalendar() {
    const {
        bookingStore: { setViewType, viewType, bookingList, getEventById, getEventTypes },
        modalStore: { openModal },
        roomStore: { getRooms },
        commonStore: { calendarDate, setCalendarDate },
        tenantStore: { selectedTenant },
        institutionStore: { selectedUserInstitution }
    } = useStore();
    const onView = useCallback((newView: any) => setViewType(newView), [setViewType])
    const [date, setDate] = useState(calendarDate ? new Date(calendarDate) : new Date());
    const popperAnchorRef = useRef<HTMLButtonElement>(null);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [popperOpener, setPopperOpener] = useState(false);
    const [showEventList, setShowEventList] = useState(false);
    const [eventList, setEventList] = useState<CalendarEvent[]>([]);
    const [calendarEvent, setCalendarEvent] = useState<CalendarEvent | null>();
    const start = moment().startOf('day').add(6, 'hours');
    const end = moment().startOf('day').add(22, 'hours');
    const min = start.toDate()
    const location = useLocation();
    const max = end.toDate()

    const handleCalendarEventSelect = async (
        userEvent: CalendarEvent,
        e: SyntheticEvent<HTMLElement, Event>
    ) => {
        if (userEvent.id === '') {
            return;
        }

        const anchor = e.currentTarget;
        const eventDetails = await getEventById(userEvent.id);
        userEvent.eventStatus = eventDetails.eventStatus;

        setAnchorEl(anchor);
        setCalendarEvent(userEvent);
        setPopperOpener(true);
    };
    const { view } = useParams();
    const [isLoading, setIsLoading] = useState(true);
    const { eventId } = location.state || {};

    useEffect(() => {
        if (view === 'day') {
            setViewType(Views.DAY);
        } else if (view === 'week') {
            setViewType(Views.WEEK);
        } else if (view === 'month') {
            setViewType(Views.MONTH);
        } else {
            setViewType(Views.DAY);
        }
        setIsLoading(false);
    }, [setViewType, view]);

    const onDateChange = (date: any) => {
        const events = bookingList.filter((event) => {
            return event.start.getDate() === date.getDate()
                && event.start.getMonth() === date.getMonth()
                && event.start.getFullYear() === date.getFullYear();
        });
        setEventList(events);
        setDate(date);
        setCalendarDate(date);
    };

    useEffect(() => {
        if (showEventList) {
            const events = bookingList.filter((event) => {
                return event.start.getDate() === date.getDate()
                    && event.start.getMonth() === date.getMonth()
                    && event.start.getFullYear() === date.getFullYear();
            });
            setEventList(events);
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bookingList, showEventList]);

    const handleSelectSlot = useCallback(
        (slot: any) => {
            if (slot.action === "select" || slot.action === "doubleClick") {
                openModal(<EventForm initialStartDate={slot.start} initialEndDate={slot.end} />)
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [openModal])

    const eventStyleGetter = (event: CalendarEvent) => {
        let style: CSSProperties = {
            backgroundColor: 'transparent',
            opacity: 0.9,
            color: darken(generateColor(event.viewId), 0.3),
            border: '0px',
            display: 'block',
            width: '95%',
            marginLeft: '10px',
            padding: '0 5px 0 0',
            borderRadius: 0,
        };

        return {
            className: '',
            style: style,
        };
    };

    useEffect(() => {
        let isMounted = true;
        const openEventForm = async (eventId: string) => {
            const event = await getEventById(eventId);
            const startDate = new Date(event?.start!)
            const endDate = new Date(event?.end!)

            if (selectedTenant) {
                await getEventTypes(selectedTenant.id);
                await getRooms(selectedTenant.id);
            }
            if (isMounted) {
                openModal(
                    <EventForm
                        event={event}
                        initialStartDate={startDate!}
                        initialEndDate={
                            event.isAllDay
                                ? dayjs(endDate!).subtract(1, "day").toDate()
                                : endDate!
                        }
                        allRecurrences={false}
                    />,
                    {
                        confirmClose: true,
                    }
                );
            }
        };

        if (eventId) {
            openEventForm(eventId);
        }
        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventId, selectedTenant]);


    const components = {
        toolbar: Toolbar,
        event: Event,
        month: { header: MonthHeader },
        week: { header: WeekHeader, dow: 1 },
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center', width: '100%', height: '100%', backgroundColor: '#f1f4f9' }}>
            <BookingHeader />
            <Grid container sx={{ backgroundColor: '#f1f4f9', flexGrow: 1, flexWrap: 'inherit' }}>
                <Grid item >
                    <CalendarDashboard displayDate={onDateChange} date={date} className='dashboard-container' />
                </Grid>
                <Grid item className="calendar-view" sx={{ width: '82vw', padding: '17px 5px' }}>
                    {isLoading ? <LoadingComponent /> :
                        <Calendar<CalendarEvent, {}>
                            className="my-calendar"
                            localizer={localizer}
                            events={bookingList}
                            startAccessor="start"
                            endAccessor="end"
                            allDayMaxRows={1}
                            allDayAccessor={(event) => event.isAllDay}
                            onShowMore={(events, date) => {
                                if (viewType === Views.MONTH) {
                                    setShowEventList(true);
                                    setEventList(events);
                                    setDate(date);
                                }
                            }}
                            popup={viewType !== Views.MONTH}
                            onSelectEvent={handleCalendarEventSelect}
                            components={components}
                            eventPropGetter={eventStyleGetter}
                            onSelectSlot={(e) => handleSelectSlot(e)}
                            dayLayoutAlgorithm={'no-overlap'}
                            style={{
                                height: '76vh',
                                width: showEventList && viewType === Views.MONTH ? '60vw' : '100%',
                                backgroundColor: '#ffffff'
                            }}
                            defaultView={Views.WEEK}
                            view={viewType}
                            drilldownView={viewType}
                            onView={onView}
                            date={date}
                            onNavigate={newDate => onDateChange(newDate)}
                            selectable={selectedUserInstitution ? hasModulePermission(["EDIT_ALL", "EDIT_OWN"], ModuleName.RESOURCE_PLANNING, selectedUserInstitution, false) : false}
                            min={min}
                            max={max}
                            formats={{
                                dayFormat: (date, culture, localizer) =>
                                    localizer!.format(date, 'DD dddd'),
                                dateFormat: (date, culture, localizer) => {
                                    if (date.getDate() === 1) {
                                        return localizer!.format(date, 'MMM D');
                                    } else {
                                        return localizer!.format(date, 'D');
                                    }
                                },
                                timeGutterFormat: (date, culture, localizer) =>
                                    localizer!.format(date, 'HH:mm'),
                            }}
                        />}
                    <EventList
                        open={showEventList && viewType === Views.MONTH}
                        handleOpen={setShowEventList}
                        events={eventList}
                        date={date}
                        handleSelect={handleCalendarEventSelect}
                        view={viewType}
                    />
                    <Box ref={popperAnchorRef}></Box>
                    <EventPopper
                        open={popperOpener}
                        handleOpen={setPopperOpener}
                        anchorElement={anchorEl}
                        eventData={calendarEvent} />
                </Grid>
            </Grid>
        </Box>
    );
};

export default observer(MyCalendar);